import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { KeyRound, Copy, Trash2, Plus, Loader2 } from "lucide-react";

export default function ApiKeysSection({ t }) {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [freshKey, setFreshKey] = useState(null);

  const load = async () => {
    try { setKeys((await api.get("/api-keys")).data.keys || []); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/api-keys", { name });
      setFreshKey(data);
      setName("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Bu anahtarı iptal etmek istediğinizden emin misiniz?")) return;
    try { await api.delete(`/api-keys/${id}`); load(); toast.success("Anahtar iptal edildi"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const copy = async (v) => { await navigator.clipboard.writeText(v); toast.success("Panoya kopyalandı"); };

  return (
    <section className="aq-card p-8 md:p-10" data-testid="api-keys-section">
      <div className="flex items-center gap-3 mb-6">
        <KeyRound size={18} className="text-[#1E3A5F]" />
        <h2 className="font-serif-display text-2xl">API Anahtarları</h2>
      </div>
      <p className="text-sm text-[#4A4A4A] mb-6 max-w-lg">
        Aqion servislerini programatik olarak çağırmak için kişisel API anahtarı üretin.
        Anahtar yalnızca bir kez tam olarak gösterilir; güvenli bir yerde saklayın.
      </p>

      <form onSubmit={create} className="flex flex-wrap gap-3 items-end mb-6">
        <div className="flex-1 min-w-[240px]">
          <label className="label-eyebrow block mb-2">Anahtar adı</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn: Production"
            className="w-full bg-transparent border-b border-[#B6B09F] focus:border-[#1E3A5F] outline-none py-2"
            data-testid="api-key-name-input" />
        </div>
        <button type="submit" disabled={loading} className="aq-button-primary" data-testid="api-key-create-btn">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Oluştur
        </button>
      </form>

      {freshKey && (
        <div className="mb-6 p-5 rounded-xl border border-[#1E3A5F] bg-[#EAE4D5]" data-testid="fresh-api-key">
          <div className="font-mono-aq text-[0.65rem] tracking-[0.22em] uppercase text-[#1E3A5F] mb-2">
            Yeni anahtar — bu sadece bir kez gösterilir
          </div>
          <div className="flex items-center gap-2 bg-white border border-[#B6B09F] rounded-lg p-3">
            <code className="flex-1 text-xs md:text-sm font-mono-aq break-all">{freshKey.key}</code>
            <button onClick={() => copy(freshKey.key)} className="text-[#0A0A0A] hover:text-[#1E3A5F]" data-testid="copy-fresh-key">
              <Copy size={16} />
            </button>
          </div>
          <button onClick={() => setFreshKey(null)} className="mt-3 text-xs text-[#4A4A4A] hover:text-[#0A0A0A]">Kapat</button>
        </div>
      )}

      <div className="space-y-2">
        {keys.length === 0 ? (
          <div className="text-sm text-[#B6B09F]">Henüz anahtar oluşturmadınız.</div>
        ) : keys.map((k) => (
          <div key={k.id} className="flex items-center gap-4 p-4 border border-[#B6B09F]/40 rounded-lg" data-testid={`api-key-${k.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{k.name}</div>
              <div className="font-mono-aq text-[0.7rem] text-[#4A4A4A] mt-1">
                {k.prefix}••••{k.last4} · {new Date(k.created_at).toLocaleDateString()}
                {k.revoked && <span className="ml-2 text-red-700">İPTAL</span>}
              </div>
            </div>
            {!k.revoked && (
              <button onClick={() => revoke(k.id)} className="text-[#0A0A0A] hover:text-red-700" data-testid={`revoke-${k.id}`}>
                <Trash2 size={16} />
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
